import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp, Overline } from "@/components/Motion";
import { PROCESS_STEPS, FACTORY_IMAGES, SITE } from "@/data/site";
import { useLang, TA, u } from "@/data/i18n";

const A = `${process.env.PUBLIC_URL}/assets`;

export default function Manufacturing() {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="manufacturing" data-testid="manufacturing-section" className="bg-paper">
      {/* Full-bleed parallax factory banner */}
      <div ref={ref} className="relative h-[70vh] md:h-[85vh] overflow-hidden">
        <motion.img
          src={`${A}/f-stack.jpg`}
          alt="Lovely Foodpacks factory — strapped paper banana leaf packets ready for dispatch, Sivakasi"
          style={{ y }}
          className="absolute inset-0 w-full h-[124%] object-cover"
        />
        <div className="absolute inset-0 bg-leaf/70" />
        <div className="relative h-full max-w-[1500px] mx-auto px-5 md:px-10 flex items-end pb-16">
          <FadeUp>
            <div className="max-w-xl bg-white/10 backdrop-blur-md border border-white/20 p-8 md:p-10 text-white">
              <Overline dark>{u(lang, "mfg_overline")}</Overline>
              <h2 className="mt-3 font-display font-black tracking-tight text-3xl md:text-5xl leading-[1.02]">
                {u(lang, "mfg_heading")}
              </h2>
              <p className="mt-4 text-white/75 leading-relaxed text-sm md:text-base">
                {u(lang, "mfg_body")}
              </p>
            </div>
          </FadeUp>
        </div>
      </div>

      {/* Process steps */}
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <FadeUp>
          <Overline>{u(lang, "process_overline")}</Overline>
          <h3 className="mt-4 font-display font-black tracking-tight text-4xl md:text-5xl text-leaf">
            {u(lang, "process_heading")}
          </h3>
        </FadeUp>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border border-ink/10">
          {PROCESS_STEPS.map((s, i) => (
            <FadeUp key={s.no} delay={0.06 * i}>
              <div
                data-testid={`process-${s.no}`}
                className="group h-full p-7 border-b sm:border-b-0 lg:border-l first:border-l-0 border-ink/10 transition-colors duration-500 hover:bg-leaf"
              >
                <span className="font-display font-black text-4xl tracking-tighter text-stroke-leaf group-hover:text-sun group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                  {s.no}
                </span>
                <h4 className="mt-4 font-display font-bold text-lg text-leaf group-hover:text-white transition-colors duration-500">
                  {lang === "ta" ? TA.process[i].title : s.title}
                </h4>
                <p className="mt-2 text-sm text-muted leading-relaxed group-hover:text-white/70 transition-colors duration-500">
                  {lang === "ta" ? TA.process[i].body : s.body}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>

        {/* Factory gallery strip */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {FACTORY_IMAGES.map((f, i) => (
            <FadeUp key={f.img} delay={0.06 * i}>
              <figure data-testid={`factory-img-${i}`} className="group border border-ink/10 overflow-hidden bg-white">
                <div className="overflow-hidden aspect-[4/5]">
                  <img
                    src={f.img}
                    alt={f.caption}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <figcaption className="px-4 py-3 text-xs font-semibold tracking-wide uppercase text-muted border-t border-ink/10">
                  {lang === "ta" ? TA.factoryCaptions[i] : f.caption}
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
